import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useAddSession,
  useAdminAttendanceUser,
  useEditSession,
  useManagerAttendanceUser,
  useRemoveSession,
  useReplaceDay,
} from "@/api/queries";
import type { AttendanceEvent, AttendanceLogRow, AttendanceSession, LeaveDay, WfhDay } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { calendarTodayStr, classifyDay, leaveByDate, wfhByDate } from "@/lib/calendar";
import { formatDate, formatTime, formatTimeInputValue } from "@/lib/format";
import { formatMinutes } from "@/lib/org-time";
import { AttendanceStatusBadge } from "@/pages/hr/attendance";

type Tab = "log" | "trail";

/**
 * One person's attendance, for HR and managers. Same component for both: the only
 * difference is which endpoint prefix it talks to, and the backend is what
 * enforces that a manager can only reach their own team.
 *
 * Two views, because they answer different questions:
 *
 *   Log    what each day comes to, and its sessions — the thing to correct.
 *   Trail  every button press ever recorded, including the ones HR removed.
 *          This is how "what actually happened on the 20th" gets answered, which
 *          the old single-row-per-day shape could not do at all: a correction
 *          overwrote the original stamps and they were gone.
 */
export function AttendanceLogDialog({
  scope,
  person,
  holidays,
  onClose,
}: {
  scope: "admin" | "manager";
  person: { id: number; name: string };
  holidays: Map<string, string>;
  onClose: () => void;
}) {
  const adminLog = useAdminAttendanceUser(scope === "admin" ? person.id : null);
  const managerLog = useManagerAttendanceUser(scope === "manager" ? person.id : null);
  const log = scope === "admin" ? adminLog : managerLog;

  const addSession = useAddSession(scope);
  const editSession = useEditSession(scope);
  const removeSession = useRemoveSession(scope);
  const replaceDay = useReplaceDay(scope);

  const [tab, setTab] = useState<Tab>("log");
  const [editor, setEditor] = useState<EditorState | null>(null);

  const sessionsByDate = log.data?.sessionsByDate ?? {};
  const events = log.data?.events ?? [];

  /*
    What mode a NEW session should default to: the account's own work mode,
    not a bare "onsite" literal. A remote employee's day is remote, and guessing
    otherwise is how this field came to contradict the database. A hybrid
    account genuinely chooses, so it falls to onsite and stays editable.
  */
  const accountMode: "onsite" | "remote" = log.data?.user.work_mode === "remote" ? "remote" : "onsite";

  // Leave for this person, by date.
  const leave = useMemo(() => leaveByDate(log.data?.leave), [log.data?.leave]);
  // Approved work-from-home days for this person, so an upcoming one reads as
  // planned and a worked one can say it was worked from home. Never merged into
  // `leave`: it is a day worked.
  const wfh = useMemo(() => wfhByDate(log.data?.wfh), [log.data?.wfh]);

  // The log is built from the `attendance` projection, which only has a row for
  // a day something was RECORDED on. A day spent on leave has nothing recorded —
  // so without this merge the one view that claims to be a person's full history
  // is precisely the view their leave is missing from. Leave days with no
  // attendance get a placeholder row; days that have both keep the real record
  // and pick the leave up as a second badge.
  const records = useMemo(() => {
    const real = log.data?.records ?? [];
    const seen = new Set(real.map((record) => record.date));
    const leaveOnly: AttendanceLogRow[] = [...leave.keys()]
      .filter((date) => !seen.has(date))
      .map((date) => ({
        date,
        check_in: null,
        check_out: null,
        mode: null,
        status: null,
        total_minutes: 0,
        edited_by: null,
        edited_at: null,
        edited_by_name: null,
      }));
    // Newest first, matching the order the endpoint already returns.
    return [...real, ...leaveOnly].sort((a, b) => b.date.localeCompare(a.date));
  }, [log.data?.records, leave]);

  const busy = addSession.isPending || editSession.isPending || removeSession.isPending || replaceDay.isPending;

  function fail(err: unknown) {
    toast.error(err instanceof ApiError ? err.message : "Could not save that.");
  }

  async function save(state: EditorState) {
    try {
      if (state.kind === "add") {
        await addSession.mutateAsync({
          userId: person.id,
          date: state.date,
          mode: state.mode,
          checkIn: state.checkIn,
          checkOut: state.checkOut,
        });
      } else if (state.kind === "edit") {
        await editSession.mutateAsync({
          userId: person.id,
          eventId: state.eventId,
          checkIn: state.checkIn,
          checkOut: state.checkOut,
        });
      } else {
        await replaceDay.mutateAsync({
          userId: person.id,
          date: state.date,
          mode: "remote",
          status: "inactive",
        });
      }
      toast.success(`Attendance updated for ${person.name}.`);
      setEditor(null);
    } catch (err) {
      fail(err);
    }
  }

  async function remove(eventId: number) {
    try {
      await removeSession.mutateAsync({ userId: person.id, eventId });
      toast.success("Session removed.");
    } catch (err) {
      fail(err);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent wide>
        <DialogHeader
          title={person.name}
          description="Every day, the sessions behind it, and every press ever recorded. Nothing is deleted — a removed session stays in the trail."
        />

        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
              <TabsList>
                <TabsTrigger value="log">Log</TabsTrigger>
                <TabsTrigger value="trail">Audit trail</TabsTrigger>
              </TabsList>
            </Tabs>
            {tab === "log" && !editor && (
              <Button
                variant="secondary"
                size="sm"
                iconLeft="plus"
                onClick={() =>
                  setEditor({
                    kind: "add",
                    // The date the admin endpoint bounds with, so a session added
                    // before 6am is not rejected as being in the future.
                    date: calendarTodayStr(),
                    mode: accountMode,
                    checkIn: "",
                    checkOut: "",
                  })
                }
              >
                Add a session
              </Button>
            )}
          </div>

          {editor && (
            <SessionEditor
              state={editor}
              busy={busy}
              onChange={setEditor}
              onCancel={() => setEditor(null)}
              onSave={save}
            />
          )}

          {log.isLoading ? (
            <SkeletonTable rows={6} cols={5} />
          ) : tab === "log" ? (
            records.length === 0 ? (
              <EmptyState
                icon="attendance"
                title="No attendance on file"
                body={`${person.name} has not recorded any attendance yet.`}
              />
            ) : (
              <DayLog
                records={records}
                sessionsByDate={sessionsByDate}
                holidays={holidays}
                leave={leave}
                wfh={wfh}
                busy={busy}
                accountMode={accountMode}
                onEdit={(date, session, mode) =>
                  setEditor({
                    kind: "edit",
                    eventId: session.id as number,
                    date,
                    // The DAY's own mode, passed up from the row. This was the
                    // literal "onsite", so correcting a remote session's times
                    // showed HR a locked field asserting the day was onsite.
                    mode,
                    checkIn: formatTimeInputValue(session.check_in),
                    checkOut: formatTimeInputValue(session.check_out),
                  })
                }
                onRemove={remove}
                onAddTo={(date, mode) => setEditor({ kind: "add", date, mode, checkIn: "", checkOut: "" })}
                onMarkOff={(date) => setEditor({ kind: "off", date })}
              />
            )
          ) : events.length === 0 ? (
            <EmptyState icon="attendance" title="Nothing recorded yet" body="No presses on file for this account." />
          ) : (
            <AuditTrail events={events} />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* The day log                                                                */
/* -------------------------------------------------------------------------- */

function DayLog({
  records,
  sessionsByDate,
  holidays,
  leave,
  wfh,
  busy,
  accountMode,
  onEdit,
  onRemove,
  onAddTo,
  onMarkOff,
}: {
  records: AttendanceLogRow[];
  sessionsByDate: Record<string, AttendanceSession[]>;
  holidays: Map<string, string>;
  leave: Map<string, LeaveDay>;
  wfh: Map<string, WfhDay>;
  busy: boolean;
  /** The account's work mode — the honest default for a day that has none. */
  accountMode: "onsite" | "remote";
  onEdit: (date: string, session: AttendanceSession, mode: "onsite" | "remote") => void;
  onRemove: (eventId: number) => void;
  onAddTo: (date: string, mode: "onsite" | "remote") => void;
  onMarkOff: (date: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(records[0]?.date ?? null);

  return (
    <TableWrap>
      <THead>
        <TH>Date</TH>
        <TH>Mode</TH>
        <TH numeric>Sessions</TH>
        <TH numeric>Total</TH>
        <TH>Status</TH>
        <TH>
          <span className="sr-only">Sessions</span>
        </TH>
      </THead>
      <TBody>
        {records.map((record) => {
          const sessions = sessionsByDate[record.date] ?? [];
          const isOpen = expanded === record.date;
          const onLeave = leave.get(record.date);
          const fromHome = wfh.get(record.date);
          return (
            <>
              <TR key={record.date}>
                <TDPrimary className="whitespace-nowrap font-mono text-xs">
                  {formatDate(record.date)}
                </TDPrimary>
                <TD>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="capitalize">{record.mode ?? "—"}</span>
                    {/* A remote day can be a remote ACCOUNT or an approved
                        work-from-home day on an onsite one. The two are the same
                        row in `attendance` and only this badge separates them. */}
                    {fromHome?.status === "approved" && (
                      <Badge tone="accent" icon="wfh">
                        WFH
                      </Badge>
                    )}
                  </span>
                </TD>
                <TD numeric>{sessions.length || "—"}</TD>
                <TD numeric className="font-mono text-xs">
                  {record.total_minutes > 0 ? formatMinutes(record.total_minutes) : "—"}
                </TD>
                <TD>
                  <AttendanceStatusBadge
                    kind={classifyDay(record.date, record, holidays, onLeave, fromHome)}
                    leave={onLeave}
                  />
                </TD>
                <TD>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconRight={isOpen ? "caret-left" : "caret-right"}
                    onClick={() => setExpanded(isOpen ? null : record.date)}
                  >
                    {isOpen ? "Hide" : "Sessions"}
                  </Button>
                </TD>
              </TR>
              {isOpen && (
                <TR key={`${record.date}-sessions`}>
                  <TD className="bg-surface-sunken/50" colSpan={6}>
                    <div className="flex flex-col gap-2 py-1">
                      {sessions.length === 0 ? (
                        <p className="text-xs text-muted">
                          No sessions on this day
                          {record.status === "inactive" ? " — recorded as a day off." : "."}
                        </p>
                      ) : (
                        sessions.map((session, index) => (
                          <div
                            key={`${session.check_in}-${index}`}
                            className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2"
                          >
                            <span className="w-4 shrink-0 text-center text-xs text-faint">{index + 1}</span>
                            <span className="font-mono text-[13px] tabular-nums text-ink">
                              {formatTime(session.check_in)}
                            </span>
                            <Icon name="arrow-right" size={16} className="shrink-0 text-faint" />
                            {session.check_out ? (
                              <span className="font-mono text-[13px] tabular-nums text-ink">
                                {formatTime(session.check_out)}
                              </span>
                            ) : (
                              <Badge tone="outline">Never closed</Badge>
                            )}
                            <span className="font-mono text-[13px] tabular-nums text-muted">
                              {session.check_out ? formatMinutes(session.minutes) : "—"}
                            </span>
                            {(session.in_source === "hr" || session.out_source === "hr") && (
                              <Badge tone="outline" icon="edit">
                                HR
                              </Badge>
                            )}
                            <div className="ml-auto flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconLeft="edit"
                                disabled={busy || session.id === null || session.id === undefined}
                                onClick={() =>
                                  onEdit(record.date, session, (record.mode as "onsite" | "remote") ?? accountMode)
                                }
                              >
                                Correct
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy || session.id === null || session.id === undefined}
                                onClick={() => onRemove(session.id as number)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft="plus"
                          disabled={busy}
                          onClick={() => onAddTo(record.date, (record.mode as "onsite" | "remote") ?? accountMode)}
                        >
                          Add a session to this day
                        </Button>
                        {record.mode !== "onsite" && (
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onMarkOff(record.date)}>
                            Replace with a day off
                          </Button>
                        )}
                      </div>
                    </div>
                  </TD>
                </TR>
              )}
            </>
          );
        })}
      </TBody>
    </TableWrap>
  );
}

/* -------------------------------------------------------------------------- */
/* The audit trail                                                            */
/* -------------------------------------------------------------------------- */

const EVENT_LABEL: Record<AttendanceEvent["type"], string> = {
  in: "Checked in",
  out: "Checked out",
  off: "Marked off",
  present: "Marked present",
};

/**
 * What one press says, in words.
 *
 * A declaration's whole content is how much of the day it claimed, so the
 * portion belongs in the label — "Marked present" on its own would make the two
 * presses behind a changed mind (full at 9am, half at 4pm) indistinguishable in
 * the one view that exists to tell them apart.
 */
function pressLabel(event: AttendanceEvent): string {
  if (event.type !== "present") return EVENT_LABEL[event.type];
  return event.portion === "half" ? "Marked half day" : "Marked full day";
}

function AuditTrail({ events }: { events: AttendanceEvent[] }) {
  return (
    <TableWrap>
      <THead>
        <TH>Work day</TH>
        <TH>Press</TH>
        <TH>Time</TH>
        <TH>Recorded by</TH>
        <TH>Status</TH>
      </THead>
      <TBody>
        {events.map((event) => (
          <TR key={event.id}>
            <TDPrimary className="whitespace-nowrap font-mono text-xs">
              {formatDate(event.work_date)}
            </TDPrimary>
            <TD>
              <span className={event.voided_at ? "text-muted line-through" : undefined}>
                {pressLabel(event)}
              </span>
            </TD>
            <TD className="font-mono text-xs">
              <span className={event.voided_at ? "text-muted line-through" : undefined}>
                {formatTime(event.at) ?? "—"}
              </span>
            </TD>
            <TD>
              {event.source === "self" ? (
                <span className="text-xs text-muted">The employee</span>
              ) : event.source === "import" ? (
                <Badge tone="outline">Migrated</Badge>
              ) : (
                <Badge tone="outline" icon="edit">
                  {/* Never a stand-in name. This is the provenance column: an actor
                      nobody recorded is unknown, and saying "HR" would be
                      inventing the one fact the row exists to report. */}
                  {event.recorded_by_name ?? "Unknown"}
                </Badge>
              )}
            </TD>
            <TD>
              {event.voided_at ? (
                <Badge tone="neutral" icon="x-circle">
                  {event.voided_by_name ? `Removed by ${event.voided_by_name}` : "Removed"}
                </Badge>
              ) : (
                <span className="text-xs text-faint">—</span>
              )}
            </TD>
          </TR>
        ))}
      </TBody>
    </TableWrap>
  );
}

/* -------------------------------------------------------------------------- */
/* The editor                                                                 */
/* -------------------------------------------------------------------------- */

type EditorState =
  | { kind: "add"; date: string; mode: "onsite" | "remote"; checkIn: string; checkOut: string }
  | { kind: "edit"; eventId: number; date: string; mode: "onsite" | "remote"; checkIn: string; checkOut: string }
  | { kind: "off"; date: string };

/** A session's times carry a date and a mode; a day off carries only a date. */
type TimedEditorState = Extract<EditorState, { checkIn: string }>;

function SessionEditor({
  state,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  state: EditorState;
  busy: boolean;
  onChange: (next: EditorState) => void;
  onCancel: () => void;
  onSave: (state: EditorState) => void;
}) {
  // Split on the discriminant here rather than inside one component, so the
  // times form is typed against a state that definitely has times.
  if (state.kind === "off") {
    return (
      <div className="rounded-xl border border-line bg-surface-sunken p-4">
        <p className="mb-3 text-[13px] font-medium text-ink">
          Replace {formatDate(state.date)} with a day off
          <span className="ml-2 font-normal text-muted">
            Every session on that day is removed. They stay in the audit trail.
          </span>
        </p>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" loading={busy} onClick={() => onSave(state)}>
            Mark as a day off
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return <SessionTimesEditor state={state} busy={busy} onChange={onChange} onCancel={onCancel} onSave={onSave} />;
}

function SessionTimesEditor({
  state,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  state: TimedEditorState;
  busy: boolean;
  onChange: (next: EditorState) => void;
  onCancel: () => void;
  onSave: (state: EditorState) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    if (!state.date) return setError("Pick a date.");
    if (!state.checkIn || !state.checkOut) return setError("Both times are required.");
    onSave(state);
  }

  return (
    <div className="rounded-xl border border-line bg-surface-sunken p-4">
      <p className="mb-3 text-[13px] font-medium text-ink">
        {state.kind === "add" ? "Add a session" : "Correct a session"}
        <span className="ml-2 font-normal text-muted">
          {state.kind === "add"
            ? "Sessions on a day can't overlap."
            : "The original times stay in the audit trail."}
        </span>
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Date" htmlFor="sess-date" required>
          <Input
            id="sess-date"
            type="date"
            value={state.date}
            disabled={state.kind === "edit"}
            onChange={(e) => onChange({ ...state, date: e.target.value })}
          />
        </Field>
        <Field label="Mode" htmlFor="sess-mode" required>
          <Select
            id="sess-mode"
            value={state.mode}
            disabled={state.kind === "edit"}
            onChange={(e) => onChange({ ...state, mode: e.target.value as "onsite" | "remote" })}
          >
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
          </Select>
        </Field>
        <Field label="Start" htmlFor="sess-in" required>
          <Input
            id="sess-in"
            type="time"
            value={state.checkIn}
            onChange={(e) => onChange({ ...state, checkIn: e.target.value })}
          />
        </Field>
        <Field label="End" htmlFor="sess-out" required>
          <Input
            id="sess-out"
            type="time"
            value={state.checkOut}
            onChange={(e) => onChange({ ...state, checkOut: e.target.value })}
          />
        </Field>
      </div>

      {/* An end time before 6am is the early hours of the next morning and still
          part of this work day, so an overnight shift is entered as 22:00 to
          03:00 — see hrms-backend/src/utils/timezone.js. */}
      <p className="mt-2 text-xs text-muted">
        For an overnight shift, enter the end time as it reads on the clock — 03:00 counts towards
        this work day, not the next one.
      </p>

      {error && (
        <p role="alert" className="mt-3 flex items-start gap-1.5 text-xs text-bad">
          <Icon name="warning-circle" size={16} />
          <span>{error}</span>
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={submit} loading={busy}>
          Save session
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
